import request from '../../../util/SuperagentUtils';
import { locationRequests } from '../../../requests/api-endpoints';

/**
 * Marketplace ("Find A Space") location search for the assistant.
 *
 * Hits the same optimized endpoint the search page uses
 * (`locationRequests.latLongSearch`), which returns every visible supplier
 * location near a point, each carrying its features, equipment types, city,
 * price and capacity. The endpoint scopes nothing to the signed-in account, so
 * this is the right source for a buyer browsing *prospective* locations rather
 * than the bulk-edit export, which only ever holds the caller's own listings.
 *
 * A radius is always sent so results stay near the requested place — without it
 * the backend applies its own default, which can pull in listings a state away
 * and make "how many locations in Los Angeles" wildly overcount.
 */

/** One search result. Loose and local — only the fields the skill reads. */
export interface MarketplaceLocation {
  locationId?: string;
  locationName?: string;
  companyName?: string;
  city?: string;
  state?: string;
  features?: string[];
  equipmentTypes?: string[];
  totalNumberOfSpaces?: number;
  pricePerDay?: number;
  pricePerMonth?: number;
  hasInstantApproval?: boolean;
  visible?: boolean;
  distance?: string;
}

/** Miles around the geocoded point to search. Wide enough to cover a metro
 *  area and its suburbs, narrow enough that a city search stays about that
 *  city. */
export const DEFAULT_SEARCH_RADIUS_MILES = 50;

export const searchMarketplaceLocations = (
  latitude: number,
  longitude: number,
  radiusMiles: number = DEFAULT_SEARCH_RADIUS_MILES,
): Promise<{ body: MarketplaceLocation[] }> =>
  request.get(
    `${locationRequests.latLongSearch}?lat=${latitude}&lon=${longitude}&radius=${radiusMiles}`,
  );

/**
 * One of the signed-in supplier's own locations, fetched in full.
 *
 * The bulk-edit export the assistant already holds carries a location's price
 * and capacity but *not* its coordinates (address fields are excluded from the
 * export — changing them triggers geocoding). The price-benchmark skill needs a
 * point to search the marketplace around, so it reads the location detail
 * endpoint the profile page uses, which returns the address latitude/longitude
 * alongside the same price fields. Loose and local — only the fields the skill
 * reads.
 */
export interface SupplierLocationDetail {
  id?: string;
  locationName?: string;
  city?: string;
  state?: string;
  // The location detail endpoint returns coordinates as strings (see
  // `src/types/Location.ts` and the map components that `parseFloat` them), so
  // consumers must coerce rather than assume `number`.
  addressLatitude?: number | string | null;
  addressLongitude?: number | string | null;
  pricePerDay?: number;
  pricePerMonth?: number;
  features?: string[];
}

/** Fetches one of the caller's own locations by id (supplier-scoped endpoint). */
export const getSupplierLocationDetail = (
  locationId: string,
): Promise<{ body: SupplierLocationDetail }> =>
  request.get(locationRequests.locationById(locationId));
